import { useEffect, useRef } from 'react';
import L from 'leaflet';

import { MAP_CENTER_LAT, MAP_CENTER_LNG, MAP_DEFAULT_ZOOM } from '@/utils';

import { AddressMapZone, drawAddressMapZones } from './addressMapZones';

const FALLBACK_CENTER: [number, number] = [-34.9011, -56.1645];
const FALLBACK_ZOOM = 13;
const PIN_ZOOM = 16;
const FLY_DURATION_SECONDS = 1.3;

const initialCenter: [number, number] =
  Number.isFinite(MAP_CENTER_LAT) && Number.isFinite(MAP_CENTER_LNG)
    ? [MAP_CENTER_LAT, MAP_CENTER_LNG]
    : FALLBACK_CENTER;

const initialZoom = Number.isFinite(MAP_DEFAULT_ZOOM)
  ? MAP_DEFAULT_ZOOM
  : FALLBACK_ZOOM;

export const addressPinStyles = `
@keyframes agm-pin-drop {
  0% { transform: translateY(-18px); opacity: 0; }
  60% { transform: translateY(2px); opacity: 1; }
  100% { transform: translateY(0); opacity: 1; }
}
@keyframes agm-pin-pulse {
  0% { transform: scale(0.6); opacity: 0.45; }
  70% { transform: scale(1.6); opacity: 0; }
  100% { transform: scale(1.6); opacity: 0; }
}
.agm-pin { position: relative; width: 34px; height: 46px; }
.agm-pin__body {
  position: absolute; inset: 0;
  animation: agm-pin-drop 0.45s cubic-bezier(0.22, 1, 0.36, 1);
  filter: drop-shadow(0 3px 4px rgba(0, 0, 0, 0.35));
}
.agm-pin__pulse {
  position: absolute; left: 50%; bottom: -4px;
  width: 22px; height: 8px; margin-left: -11px;
  border-radius: 50%; background: rgba(255, 117, 0, 0.55);
  animation: agm-pin-pulse 1.8s ease-out infinite;
}
`;

export const addressPinIcon = L.divIcon({
  className: '',
  html: `
    <div class="agm-pin">
      <div class="agm-pin__pulse"></div>
      <div class="agm-pin__body">
        <svg viewBox="0 0 34 46" width="34" height="46" xmlns="http://www.w3.org/2000/svg">
          <path
            d="M17 1C8.4 1 1.5 7.9 1.5 16.5c0 10.8 12.3 24.6 14.4 26.9a1.5 1.5 0 0 0 2.2 0c2.1-2.3 14.4-16.1 14.4-26.9C32.5 7.9 25.6 1 17 1Z"
            fill="#ff7500" stroke="#ffffff" stroke-width="2.5"
          />
          <circle cx="17" cy="16.5" r="5.5" fill="#ffffff" />
        </svg>
      </div>
    </div>
  `,
  iconSize: [34, 46],
  iconAnchor: [17, 44],
});

export interface AddressGeocodeMapProps {
  coordinates: { lat: number; lng: number } | null;
  isLoading?: boolean;
  error?: string | null;
  resolvedAddress?: string;
  hasSufficientData?: boolean;
  heightClassName?: string;
  className?: string;
  zones?: AddressMapZone[];
}

const AddressGeocodeMap = ({
  coordinates,
  isLoading = false,
  error,
  resolvedAddress,
  hasSufficientData = false,
  heightClassName = 'h-72',
  className = '',
  zones,
}: AddressGeocodeMapProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<L.Map | null>(null);
  const markerRef = useRef<L.Marker | null>(null);
  const zonesLayerRef = useRef<L.LayerGroup | null>(null);

  useEffect(() => {
    if (!containerRef.current || mapRef.current) {
      return;
    }

    const map = L.map(containerRef.current, {
      center: initialCenter,
      zoom: initialZoom,
      zoomControl: true,
      scrollWheelZoom: true,
      zoomAnimation: true,
      markerZoomAnimation: true,
      fadeAnimation: true,
    });

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '© OpenStreetMap contributors',
      maxZoom: 19,
    }).addTo(map);

    zonesLayerRef.current = L.layerGroup().addTo(map);
    mapRef.current = map;

    map.whenReady(() => {
      window.setTimeout(() => mapRef.current?.invalidateSize(), 0);
    });

    const resizeObserver = new ResizeObserver(() => {
      mapRef.current?.invalidateSize();
    });
    resizeObserver.observe(containerRef.current);

    return () => {
      resizeObserver.disconnect();
      map.remove();
      mapRef.current = null;
      markerRef.current = null;
      zonesLayerRef.current = null;
    };
  }, []);

  useEffect(() => {
    const group = zonesLayerRef.current;
    if (!group) return;
    drawAddressMapZones(group, zones ?? []);
  }, [zones]);

  const lat = coordinates?.lat;
  const lng = coordinates?.lng;

  useEffect(() => {
    const map = mapRef.current;
    if (!map) {
      return;
    }

    if (lat === undefined || lng === undefined) {
      markerRef.current?.remove();
      markerRef.current = null;
      return;
    }

    const target = L.latLng(lat, lng);

    if (markerRef.current) {
      markerRef.current.setLatLng(target);
    } else {
      markerRef.current = L.marker(target, {
        icon: addressPinIcon,
        zIndexOffset: 1000,
        keyboard: false,
        interactive: false,
      }).addTo(map);
    }

    map.stop();
    map.invalidateSize();

    const alreadyCentered =
      map.getCenter().distanceTo(target) < 1 && map.getZoom() >= PIN_ZOOM;
    if (alreadyCentered) {
      return;
    }

    const frame = window.requestAnimationFrame(() => {
      mapRef.current?.flyTo(target, PIN_ZOOM, {
        duration: FLY_DURATION_SECONDS,
        easeLinearity: 0.25,
      });
    });

    return () => window.cancelAnimationFrame(frame);
  }, [lat, lng]);

  const showIdleOverlay = !coordinates && !isLoading && !error;
  const idleHint = hasSufficientData
    ? 'Presioná «Obtener ubicación» para ubicar el punto en el mapa.'
    : 'Completá departamento, ciudad, calle y número para geocodificar.';

  return (
    <div
      className={`relative isolate overflow-hidden rounded-xl border border-gray-200 shadow-sm ${className}`.trim()}
    >
      <style>{addressPinStyles}</style>

      <div ref={containerRef} className={`${heightClassName} w-full min-w-0 z-0`} />

      {showIdleOverlay && (
        <div className='absolute inset-0 z-[500] flex items-center justify-center bg-white/70 backdrop-blur-[2px]'>
          <div className='mx-6 flex flex-col items-center gap-2 text-center'>
            <svg
              viewBox='0 0 34 46'
              width='26'
              height='36'
              xmlns='http://www.w3.org/2000/svg'
              aria-hidden
            >
              <path
                d='M17 1C8.4 1 1.5 7.9 1.5 16.5c0 10.8 12.3 24.6 14.4 26.9a1.5 1.5 0 0 0 2.2 0c2.1-2.3 14.4-16.1 14.4-26.9C32.5 7.9 25.6 1 17 1Z'
                fill='#d1d5db'
              />
              <circle cx='17' cy='16.5' r='5.5' fill='#ffffff' />
            </svg>
            <p className='text-sm text-gray-600'>{idleHint}</p>
          </div>
        </div>
      )}

      {isLoading && (
        <div className='absolute inset-0 z-[500] flex items-center justify-center bg-white/60 backdrop-blur-[1px]'>
          <div className='flex items-center gap-3 rounded-full bg-white px-4 py-2 shadow-md'>
            <span className='h-4 w-4 animate-spin rounded-full border-2 border-orange-500 border-t-transparent' />
            <span className='text-sm font-medium text-gray-700'>
              Buscando ubicación...
            </span>
          </div>
        </div>
      )}

      {!isLoading && error && (
        <div className='absolute inset-x-3 top-3 z-[500]'>
          <div className='rounded-lg border border-red-200 bg-red-50/95 px-3 py-2 text-sm text-red-700 shadow-sm'>
            {error}
          </div>
        </div>
      )}

      {!isLoading && !error && coordinates && (
        <div className='absolute inset-x-0 bottom-0 z-[500] flex items-center justify-between gap-3 bg-white/90 px-4 py-2 backdrop-blur-sm'>
          <span className='truncate text-sm text-gray-700'>
            {resolvedAddress ?? 'Ubicación geocodificada'}
          </span>
          <span className='shrink-0 rounded-full bg-gray-100 px-2.5 py-0.5 font-mono text-xs text-gray-600'>
            {coordinates.lat.toFixed(5)}, {coordinates.lng.toFixed(5)}
          </span>
        </div>
      )}
    </div>
  );
};

export default AddressGeocodeMap;
